import clsx from 'clsx'
import { DatePickerButton, DatePickerCalendar, Popover } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import 'components/DatePicker/datepicker.scss'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import format from 'utils/dateFns/format'
import locales from 'utils/dateFns/locales'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import styles from 'components/DatePicker/datepicker.module.scss'
import { defaultStaticRanges } from 'components/DatePicker/defineds'
import { selectAppLocale } from 'modules/App/store/selectors'

const DatePicker = ({
  touched,
  disabled = false,
  error,
  helperText,
  fullWidth,
  label,
  hidden,
  readOnly,
  required = false,
  value = '',
  InputProps,
  placeholder,
  buttonClassName,
  onChange,
  isShown = null,
  handleHide = () => {},
  onOpen = () => {},
  minDate = undefined,
  maxDate = undefined,
  hasBackdrop = false,
  mode,
  asButton = true,
  popoverConfig = {
    variant: 'popover',
    id: 'datePickerPopover',
    breakpoints: {
      0: {
        variant: 'bottomSheet',
        hasBackdrop: true,
      },
      960: {
        variant: 'popover',
        hasBackdrop: false,
      },
    },
    disabled,
  },
}) => {
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const breakpoints = useBreakpoint()
  const i18n = useSelector(selectAppLocale)
  const { t } = useTranslation()
  const popoverState = usePopupState(popoverConfig)
  const [range, setRange] = useState(value)
  const [formattedRange, setFormattedRange] = useState({})
  const staticRanges = useMemo(() => defaultStaticRanges(t, i18n), [t, i18n])

  useEffect(() => {
    if (value && !(value instanceof Date)) {
      if (!value.startDate && !value.endDate) {
        setFormattedRange(t(label))

        return
      }
    }

    setFormattedRange(
      value && !(value instanceof Date)
        ? {
            startDate: !value.startDate
              ? ''
              : format(value.startDate, 'dd MMM yyyy').replace('.', ''),
            endDate: !value.startDate ? '' : format(value.endDate, 'dd MMM yyyy').replace('.', ''),
          }
        : value
        ? format(value, 'dd.MM.yyyy')
        : '',
    )
  }, [value])

  const onChangeHandle = useCallback(({ selection }) => setRange(selection), [setRange])

  const applyRange = useCallback(
    (range) => {
      onChange(range)
      popoverState.close()
    },
    [onChange, range, popoverState],
  )

  const handleClose = () => {
    handleHide()
    popoverState.close()
  }

  useEffect(() => {
    if (isShown) {
      popoverState.open()
    } else {
      popoverState.close()
    }
  }, [isShown])

  useEffect(() => popoverState.isOpen && onOpen(), [popoverState])

  return (
    <>
      <DatePickerButton
        touched={touched}
        error={error}
        helperText={helperText}
        fullWidth={fullWidth}
        hidden={hidden}
        readOnly={readOnly}
        placeholder={placeholder}
        label={t(label)}
        ControlProps={InputProps}
        value={formattedRange}
        bindTrigger={bindTrigger(popoverState)}
        isOpen={popoverState.isOpen}
        className={clsx(styles['datepicker-button'], buttonClassName)}
        asButton={asButton}
        size={InputProps?.size}
        required={required}
      />
      <Popover
        {...bindPopover(popoverState)}
        className={clsx(styles['datepicker-popover'], {
          [styles['datepicker-popover_shown']]: popoverState.isOpen,
        })}
        bodyClassName={styles['datepicker-popover__inner']}
        backdropClassName={styles['datepicker-popover__backdrop']}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 8,
          horizontal: 'center',
        }}
        onClose={handleClose}
        hasBackdrop={hasBackdrop}
      >
        <DatePickerCalendar
          minDate={minDate}
          maxDate={maxDate}
          onChange={onChangeHandle}
          onApply={applyRange}
          locale={locales[i18n]}
          buttonLabels={{
            dropSelection: t('common::buttons.reset'),
            applySelection: t('common::buttons.apply'),
          }}
          value={value}
          months={!breakpoints.md ? 1 : 2}
          defaultStaticRanges={!breakpoints.md ? [] : staticRanges}
          breakpoints={breakpoints}
          mode={mode}
        />
      </Popover>
    </>
  )
}

DatePicker.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      startDate: PropTypes.instanceOf(Date),
      endDate: PropTypes.instanceOf(Date),
    }),
  ]),
  mode: PropTypes.string,
  buttonClassName: PropTypes.string,
  onChange: PropTypes.func,
  isShown: PropTypes.bool,
  touched: PropTypes.bool,
  fullWidth: PropTypes.bool,
  hidden: PropTypes.bool,
  readOnly: PropTypes.bool,
  submitting: PropTypes.bool,
  handleHide: PropTypes.func,
  onOpen: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  hasBackdrop: PropTypes.bool,
  disabled: PropTypes.bool,
  InputProps: PropTypes.object,
  popoverConfig: PropTypes.object,
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
  placeholder: PropTypes.string,
  label: PropTypes.string,
  asButton: PropTypes.bool,
  required: PropTypes.bool,
}

export default DatePicker
